/**
 * Onda Polish — SEA IMPACT FX: the two things that were still costing a draw call per particle, rebuilt as
 * two (and one) instanced meshes with the whole animation in the vertex shader.
 *
 *   SplashFx      — a cannonball that MISSES throws a column of water and rings that ride out from it; one
 *                   that HITS a hull throws spray, wood splinters and sparks. TWO draw calls for every
 *                   impact on screen, and the CPU only writes at spawn: position, velocity, birth. The
 *                   ballistic arc, the growth, the fade and the billboard are all vertex ALU.
 *   ContactShadow — the soft dark ellipse under every hull. ONE draw call for the whole fleet. It is the
 *                   cheapest possible answer to "the ship looks like a sticker on the water", which is what
 *                   a hull with no occlusion cue under it always reads as.
 *
 * WHY THIS REPLACES SOMETHING RATHER THAN ADDING TO IT. The pooled `spawnFx` mesh in render.ts is one
 * THREE.Mesh with its own material PER PARTICLE — so a single confirmed hit (2 flashes + 8 splinters +
 * 3 sparks + smoke) was up to FOURTEEN draw calls, and a broadside landing three balls was forty. Routing
 * the impact particles through here is a net REDUCTION in the worst case the budget actually cares about
 * (a fight), while adding a constant 2 in the calm case where there is headroom.
 *
 * NO TEXTURES. The droplet is a smoothstep on the quad's own uv and the ring is an analytic annulus — the
 * boot budget is untouched (see the cold-load diet in CLAUDE.md).
 */
import * as THREE from 'three';

/** Concurrent particles / rings. Both are hard ceilings: the oldest slot is recycled, never grown. */
export const SPLASH_DROPS = 168;
export const SPLASH_RINGS = 20;
/** Hulls that can carry a contact shadow at once (nearest to the camera). */
export const SHADOW_MAX = 20;
/** Gravity for the ballistic particles (m/s²) — heavier than real so spray falls back inside its own life. */
const G = 26;

/** kind byte carried per drop: how the fragment tints it and whether it grows as it ages. */
const KIND_SPRAY = 0, KIND_SPLINTER = 1, KIND_SPARK = 2, KIND_FLASH = 3, KIND_SMOKE = 4;

/** Text-only render proof (headless): what this system is currently costing. */
export interface SeaFxStats { drops: number; rings: number; shadows: number; drawCalls: number }

/* ------------------------------------------------------------------------------------------------ *
 *  SPLASH — misses and hits
 * ------------------------------------------------------------------------------------------------ */

export class SplashFx {
  readonly drops: THREE.InstancedMesh;
  readonly rings: THREE.InstancedMesh;
  private dropMat: THREE.ShaderMaterial;
  private ringMat: THREE.ShaderMaterial;
  private dOrig: Float32Array;
  private dVel: Float32Array;
  private dLife: Float32Array;   // (birth, life, size, kind)
  private rPos: Float32Array;    // (x, y, z, birth)
  private rParam: Float32Array;  // (life, r0, r1, strength)
  private dHead = 0;
  private rHead = 0;
  private dLiveUntil = -1;
  private rLiveUntil = -1;
  private t = 0;
  private low = false;
  private liveDrops = 0;
  private liveRings = 0;

  constructor(parent: THREE.Object3D) {
    /* ---------------- drops: one camera-facing quad per particle ---------------- */
    {
      const geo = new THREE.PlaneGeometry(1, 1);
      this.dOrig = new Float32Array(SPLASH_DROPS * 3);
      this.dVel = new Float32Array(SPLASH_DROPS * 3);
      this.dLife = new Float32Array(SPLASH_DROPS * 4);   // life 0 => the vertex stage discards the slot
      geo.setAttribute('aOrig', new THREE.InstancedBufferAttribute(this.dOrig, 3).setUsage(THREE.DynamicDrawUsage));
      geo.setAttribute('aVel', new THREE.InstancedBufferAttribute(this.dVel, 3).setUsage(THREE.DynamicDrawUsage));
      geo.setAttribute('aLife', new THREE.InstancedBufferAttribute(this.dLife, 4).setUsage(THREE.DynamicDrawUsage));
      this.dropMat = new THREE.ShaderMaterial({
        // three's fog uniforms are merged in (and the fog chunks included) so distant spray hazes into the
        // same horizon colour the sea does — without them refreshFogUniforms would throw on this material.
        uniforms: THREE.UniformsUtils.merge([
          THREE.UniformsLib.fog,
          {
            uTime: { value: 0 },
            uSpray: { value: new THREE.Color(0xeaf6ff) },
            uWood: { value: new THREE.Color(0xa87b45) },
            uSpark: { value: new THREE.Color(0xfff0b8) },
            uFlash: { value: new THREE.Color(0xffd898) },
            uSmoke: { value: new THREE.Color(0x6e716f) },
            uNight: { value: 0 },
          },
        ]),
        vertexShader: /* glsl */ `
          #include <common>
          #include <fog_pars_vertex>
          attribute vec3 aOrig, aVel;
          attribute vec4 aLife;      // x birth, y life, z size, w kind
          uniform float uTime;
          varying float vK, vKind, vFizz;
          varying vec2 vUv;
          void main(){
            float age = uTime - aLife.x;
            // dead slot -> four clipped vertices (see the class note): no buffer compaction, ever
            if (aLife.y <= 0.0 || age < 0.0 || age > aLife.y) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
            vUv = uv;
            float k = age / aLife.y;
            vK = k; vKind = aLife.w;
            // ballistic: thrown, then pulled back into the sea inside its own lifetime
            // (a FLASH is not thrown — it is light, and light does not fall)
            float gk = aLife.w > 2.5 ? 0.0 : ${G.toFixed(1)};
            vec3 p = aOrig + aVel * age - vec3(0.0, 0.5 * gk * age * age, 0.0);
            // spray SWELLS as it atomises and a flash BLOOMS; a splinter and a spark keep their size
            float grow = aLife.w < 0.5 ? (0.45 + k * 1.05)
                       : (aLife.w > 3.5 ? (0.5 + k * 1.5)
                       : (aLife.w > 2.5 ? (0.35 + k * 1.9) : (1.0 - 0.25 * k)));
            float s = aLife.z * grow;
            vFizz = fract(aLife.x * 91.7 + aLife.z);      // per-particle jitter for the fragment's break-up
            // camera-facing WITHOUT a billboard matrix: offset in VIEW space, which is two adds
            vec4 mvPosition = viewMatrix * vec4(p, 1.0);
            vec2 off = position.xy * s;
            // debris is a SLIVER, not a pellet: stretch the quad along its own projected travel (~6 ALU)
            if (aLife.w > 0.5 && aLife.w < 2.5) {
              vec3 vv = aVel - vec3(0.0, gk * age, 0.0);
              vec2 dv = (viewMatrix * vec4(vv, 0.0)).xy;
              float L = length(dv);
              if (L > 1e-3) {
                vec2 u = dv / L;
                off = u * (position.y * s * 2.7) + vec2(-u.y, u.x) * (position.x * s * 0.5);
              }
            }
            mvPosition.xy += off;
            gl_Position = projectionMatrix * mvPosition;
            #include <fog_vertex>
          }
        `,
        fragmentShader: /* glsl */ `
          precision mediump float;
          #include <common>
          #include <fog_pars_fragment>
          uniform vec3 uSpray, uWood, uSpark, uFlash, uSmoke; uniform float uNight;
          varying float vK, vKind, vFizz;
          varying vec2 vUv;
          void main(){
            vec2 p = vUv * 2.0 - 1.0;
            float d = dot(p, p);
            if (d > 1.0) discard;
            // soft droplet: (1 - r²)², the cheapest kernel with no silhouette. Splinters keep an edge.
            float soft = 1.0 - d;
            float core = vKind > 3.5 ? soft * soft * 0.9
                       : (vKind > 2.5 ? soft * soft * soft
                       : (vKind > 0.5 ? smoothstep(0.35, 0.95, soft) : soft * soft));
            vec3 col = vKind < 0.5 ? uSpray
                     : (vKind < 1.5 ? uWood
                     : (vKind < 2.5 ? uSpark : (vKind < 3.5 ? uFlash : uSmoke)));
            // spray fades, spark burns out hard, splinter falls, flash is gone, smoke thins
            float fadeK = vKind > 1.5 && vKind < 3.5 ? (1.0 - vK) * (1.0 - vK) : 1.0 - vK * vK;
            float a = core * fadeK
                    * (vKind < 0.5 ? 0.55 : (vKind > 3.5 ? 0.5 : (vKind > 2.5 ? 0.95 : 0.9)))
                    * (0.75 + 0.35 * vFizz);
            if (a < 0.01) discard;
            col *= vKind < 0.5 ? (1.0 - 0.45 * uNight) : 1.0;
            gl_FragColor = vec4(pow(col, vec3(2.2)) * (vKind > 1.5 && vKind < 3.5 ? 2.4 : 1.0), a);
            #include <fog_fragment>
          }
        `,
        transparent: true, depthWrite: false, fog: true, side: THREE.DoubleSide,
      });
      this.drops = new THREE.InstancedMesh(geo, this.dropMat, SPLASH_DROPS);
      this.drops.frustumCulled = false;
      this.drops.renderOrder = 6;
      this.drops.visible = false;
      // the instance MATRIX is identity for every slot — the shader places each particle from aOrig/aVel,
      // so there is no per-frame matrix upload at all
      const idm = new THREE.Matrix4();
      for (let i = 0; i < SPLASH_DROPS; i++) this.drops.setMatrixAt(i, idm);
      this.drops.instanceMatrix.needsUpdate = true;
      parent.add(this.drops);
    }
    /* ---------------- rings: flat expanding annuli lying on the sea ---------------- */
    {
      const geo = new THREE.PlaneGeometry(1, 1);
      this.rPos = new Float32Array(SPLASH_RINGS * 4);
      this.rParam = new Float32Array(SPLASH_RINGS * 4);
      geo.setAttribute('aRing', new THREE.InstancedBufferAttribute(this.rPos, 4).setUsage(THREE.DynamicDrawUsage));
      geo.setAttribute('aParam', new THREE.InstancedBufferAttribute(this.rParam, 4).setUsage(THREE.DynamicDrawUsage));
      this.ringMat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uColor: { value: new THREE.Color(0xe8f4ff) }, uNight: { value: 0 } },
        vertexShader: /* glsl */ `
          attribute vec4 aRing;    // xyz = centre on the sea, w = birth
          attribute vec4 aParam;   // x life, y r0, z r1, w strength
          uniform float uTime;
          varying vec2 vUv; varying float vK, vStr, vSeed;
          void main(){
            float age = uTime - aRing.w;
            if (aParam.x <= 0.0 || age < 0.0 || age > aParam.x) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
            float k = age / aParam.x;
            // radius eases OUT: fast away, then losing against the water's drag
            float r = mix(aParam.y, aParam.z, 1.0 - (1.0 - k) * (1.0 - k));
            vUv = uv; vK = k; vStr = aParam.w; vSeed = fract(aRing.w * 37.1);
            vec3 world = aRing.xyz + vec3(position.x, 0.0, position.y) * (r * 2.0);
            gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          precision mediump float;
          uniform vec3 uColor; uniform float uNight;
          varying vec2 vUv; varying float vK, vStr, vSeed;
          void main(){
            vec2 p = vUv * 2.0 - 1.0;
            float r = length(p);
            if (r > 1.0) discard;
            // the RIM is the ring: a band that thins as it expands, so it dissolves rather than shrinks
            float band = smoothstep(0.70, 0.985, r) * smoothstep(1.0, 0.955, r);
            // broken round its circumference; the seed keeps any two rings from matching
            float ang = atan(p.y, p.x);
            float chew = 0.55 + 0.45 * sin(ang * 7.0 + vSeed * 31.0) * sin(ang * 3.0 - vSeed * 17.0);
            float a = band * chew * vStr * 1.5 * (1.0 - vK) * (1.0 - vK) * (1.0 - 0.4 * uNight);
            if (a < 0.008) discard;
            gl_FragColor = vec4(pow(uColor, vec3(2.2)), a);
          }
        `,
        transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
        polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2,
      });
      this.rings = new THREE.InstancedMesh(geo, this.ringMat, SPLASH_RINGS);
      this.rings.frustumCulled = false;
      this.rings.renderOrder = 2;
      this.rings.visible = false;
      const idm = new THREE.Matrix4();
      for (let i = 0; i < SPLASH_RINGS; i++) this.rings.setMatrixAt(i, idm);
      this.rings.instanceMatrix.needsUpdate = true;
      parent.add(this.rings);
    }
  }

  /** Low preset: half the particles (the fill, not the feedback — a hit still reads). */
  setQuality(q: 'low' | 'high'): void { this.low = q === 'low'; }

  /** One call per frame, before render. Also parks both meshes when nothing is alive (0 draw calls). */
  setEnv(t: number, night: number): void {
    this.t = t;
    this.dropMat.uniforms.uTime!.value = t;
    this.ringMat.uniforms.uTime!.value = t;
    this.dropMat.uniforms.uNight!.value = night;
    this.ringMat.uniforms.uNight!.value = night;
    this.drops.visible = t <= this.dLiveUntil;
    this.rings.visible = t <= this.rLiveUntil;
    if (!this.drops.visible) this.liveDrops = 0;
    if (!this.rings.visible) this.liveRings = 0;
  }

  stats(): SeaFxStats {
    return {
      drops: this.liveDrops, rings: this.liveRings, shadows: 0,
      drawCalls: (this.drops.visible ? 1 : 0) + (this.rings.visible ? 1 : 0),
    };
  }

  /** A shot that MISSES: the column of water it throws up, and the rings running out from the entry. */
  miss(x: number, y: number, z: number, big: boolean): void {
    const n = this.low ? (big ? 9 : 6) : (big ? 20 : 13);
    const up = big ? 15 : 11;
    // the COLUMN: a tight cluster thrown almost straight up, so it reads as one plume rather than a puff
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + this.t * 3.1;
      const spread = (0.35 + (i % 4) * 0.42) * (big ? 1.5 : 1.0);
      this.drop(x + Math.cos(a) * spread * 0.5, y + 0.2, z + Math.sin(a) * spread * 0.5,
        Math.cos(a) * spread * 1.05, up * (0.62 + (i % 5) * 0.11), Math.sin(a) * spread * 1.05,
        big ? 1.25 : 0.85, big ? 0.95 : 0.72, KIND_SPRAY);
    }
    this.ring(x, y + 0.12, z, big ? 1.6 : 1.1, big ? 13 : 8.5, big ? 0.95 : 0.75, big ? 0.85 : 0.62);
    if (!this.low) this.ring(x, y + 0.1, z, big ? 0.8 : 0.6, big ? 7 : 4.5, big ? 0.55 : 0.45, 0.5);
  }

  /**
   * A shot that HITS a hull: spray off the waterline, wood splinters and sparks thrown along the ball's
   * travel (a cone, not a sphere — you should be able to read where the shot came from), plus a small
   * ring at the waterline so the impact is anchored to the sea and not floating in the air.
   */
  hit(x: number, y: number, z: number, seaY: number, dirX: number, dirZ: number, confirmed: boolean): void {
    const dl = Math.hypot(dirX, dirZ);
    const base = dl > 0.01 ? Math.atan2(dirZ, dirX) : 0;
    // THE FLASH. It used to be two pooled SPHERES — 8×6-segment balls scaled to 11 m — which from the deck
    // at close range read as a faceted amber beach ball hanging off the hull (photographed). A muzzle/impact
    // flash is LIGHT: it has no silhouette, no facets, and it is gone in three frames. Two soft billboards
    // in the same instanced buffer as the debris, so this also costs two draw calls fewer than before.
    this.drop(x, y, z, 0, 0, 0, confirmed ? 4.6 : 3.4, confirmed ? 0.11 : 0.09, KIND_FLASH);
    this.drop(x, y, z, 0, 0, 0, confirmed ? 2.6 : 2.0, confirmed ? 0.26 : 0.21, KIND_FLASH);
    const nSpl = this.low ? (confirmed ? 5 : 4) : (confirmed ? 11 : 8);
    for (let i = 0; i < nSpl; i++) {
      const a = dl > 0.01 ? base + (i / nSpl - 0.5) * 1.55 : (i / nSpl) * Math.PI * 2;
      const sp = 9 + (i % 3) * 4;
      this.drop(x, y + 0.3, z, Math.cos(a) * sp, 7 + (i % 3) * 4, Math.sin(a) * sp,
        0.80 + (i % 3) * 0.22, 0.95, KIND_SPLINTER);
    }
    const nSpk = this.low ? 2 : 5;
    for (let i = 0; i < nSpk; i++) {
      const a = dl > 0.01 ? base + (i - 2) * 0.26 : i * 1.4;
      const sp = 17 + i * 3;
      this.drop(x, y + 0.2, z, Math.cos(a) * sp, 4 + i * 2.5, Math.sin(a) * sp, 0.32, 0.24, KIND_SPARK);
    }
    const nSpray = this.low ? 4 : 9;
    for (let i = 0; i < nSpray; i++) {
      const a = (i / nSpray) * Math.PI * 2;
      this.drop(x + Math.cos(a) * 1.2, seaY + 0.25, z + Math.sin(a) * 1.2,
        Math.cos(a) * 3.4, 8 + (i % 3) * 2.4, Math.sin(a) * 3.4, 1.15, 0.7, KIND_SPRAY);
    }
    // the powder smoke the strike leaves hanging — the LAST pooled single-mesh particle a hit used to
    // spend, now a soft billboard in the same buffer. (The pooled sphere was an 8x6 ball scaled to 5 m: at
    // close range it read as a faceted grey beach ball stuck to the hull.)
    this.drop(x, y + 1.0, z, 0, 1.5, 0, confirmed ? 3.6 : 2.9, 0.75, KIND_SMOKE);
    this.ring(x, seaY + 0.12, z, 1.2, confirmed ? 8 : 6, 0.7, confirmed ? 0.7 : 0.5);
  }

  /** A near-miss whistling past the hull: a thin tall column, no ring (the whistle owns that beat). */
  geyser(x: number, y: number, z: number): void {
    const n = this.low ? 5 : 11;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      this.drop(x + Math.cos(a) * 0.35, y + 0.2, z + Math.sin(a) * 0.35,
        Math.cos(a) * 1.1, 13 * (0.7 + (i % 4) * 0.1), Math.sin(a) * 1.1, 0.95, 0.8, KIND_SPRAY);
    }
    this.ring(x, y + 0.1, z, 0.9, 6.5, 0.65, 0.45);
  }

  /* ------------------------------------------------------------------ */

  private drop(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, life: number, kind: number): void {
    const i = this.dHead;
    this.dHead = (this.dHead + 1) % SPLASH_DROPS;
    const o = i * 3, l = i * 4;
    this.dOrig[o] = x; this.dOrig[o + 1] = y; this.dOrig[o + 2] = z;
    this.dVel[o] = vx; this.dVel[o + 1] = vy; this.dVel[o + 2] = vz;
    this.dLife[l] = this.t; this.dLife[l + 1] = life; this.dLife[l + 2] = size; this.dLife[l + 3] = kind;
    this.dLiveUntil = Math.max(this.dLiveUntil, this.t + life);
    this.liveDrops++;
    this.dropsDirty = true;
  }

  private ring(x: number, y: number, z: number, r0: number, r1: number, life: number, strength: number): void {
    const i = this.rHead;
    this.rHead = (this.rHead + 1) % SPLASH_RINGS;
    const o = i * 4;
    this.rPos[o] = x; this.rPos[o + 1] = y; this.rPos[o + 2] = z; this.rPos[o + 3] = this.t;
    this.rParam[o] = life; this.rParam[o + 1] = r0; this.rParam[o + 2] = r1; this.rParam[o + 3] = strength;
    this.rLiveUntil = Math.max(this.rLiveUntil, this.t + life);
    this.liveRings++;
    this.ringsDirty = true;
  }

  private dropsDirty = false;
  private ringsDirty = false;

  /** Upload whatever was spawned this frame. Called once, after all spawns, before render. */
  flush(): void {
    if (this.dropsDirty) {
      const g = this.drops.geometry;
      (g.getAttribute('aOrig') as THREE.BufferAttribute).needsUpdate = true;
      (g.getAttribute('aVel') as THREE.BufferAttribute).needsUpdate = true;
      (g.getAttribute('aLife') as THREE.BufferAttribute).needsUpdate = true;
      this.dropsDirty = false;
    }
    if (this.ringsDirty) {
      const g = this.rings.geometry;
      (g.getAttribute('aRing') as THREE.BufferAttribute).needsUpdate = true;
      (g.getAttribute('aParam') as THREE.BufferAttribute).needsUpdate = true;
      this.ringsDirty = false;
    }
  }

  /** Kill everything on the spot (stepping ashore / a scene teardown). */
  clear(): void {
    this.dLife.fill(0); this.rParam.fill(0);
    this.dLiveUntil = -1; this.rLiveUntil = -1;
    this.liveDrops = 0; this.liveRings = 0;
    this.dropsDirty = this.ringsDirty = true;
    this.flush();
    this.drops.visible = false; this.rings.visible = false;
  }

  dispose(): void {
    this.drops.parent?.remove(this.drops);
    this.rings.parent?.remove(this.rings);
    this.drops.geometry.dispose(); this.dropMat.dispose(); this.drops.dispose();
    this.rings.geometry.dispose(); this.ringMat.dispose(); this.rings.dispose();
  }
}

/* ------------------------------------------------------------------------------------------------ *
 *  CONTACT SHADOW — the hull's own dark ellipse on the water
 * ------------------------------------------------------------------------------------------------ */

/**
 * A ship drawn on a lit sea with no occlusion under it reads as a decal — it is the single most common
 * "why does this look wrong" in a stylised water game, and it has nothing to do with the water.
 *
 * A real shadow map is out (a whole depth pass, on a mobile budget, for a shape that is always an oval).
 * This is the standard cheap answer and it is honest for this case: ONE instanced quad per hull lying on
 * the sea, with an elliptical falloff in the fragment. It is not a light-transport shadow, it is AMBIENT
 * OCCLUSION — the sky the hull blocks — which is why it sits UNDER the ship and only leans a little
 * downsun instead of stretching out across the water at dawn.
 *
 * It reads the swell: on a crest the hull is further from the water it occludes, so the patch spreads and
 * lightens; in a trough it tightens and darkens. That is what stops it being a sticker of its own.
 */
export class ContactShadows {
  readonly mesh: THREE.InstancedMesh;
  private mat: THREE.ShaderMaterial;
  private alpha: Float32Array;
  private dummy = new THREE.Object3D();
  private n = 0;

  constructor(parent: THREE.Object3D) {
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.rotateX(-Math.PI / 2);                       // lie flat on the sea (XZ)
    this.alpha = new Float32Array(SHADOW_MAX);
    geo.setAttribute('aDark', new THREE.InstancedBufferAttribute(this.alpha, 1).setUsage(THREE.DynamicDrawUsage));
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(0x061a26) } },
      vertexShader: /* glsl */ `
        attribute float aDark;
        varying vec2 vUv; varying float vDark;
        void main(){
          vUv = uv; vDark = aDark;
          gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision mediump float;
        uniform vec3 uColor;
        varying vec2 vUv; varying float vDark;
        void main(){
          vec2 p = vUv * 2.0 - 1.0;
          float d = dot(p, p);
          if (d > 1.0 || vDark <= 0.004) discard;
          // (1 - r²)² — soft, no rim; squared again at the core so the dark sits under the keel
          float k = 1.0 - d;
          float a = k * k * (0.45 + 0.55 * k) * vDark;
          if (a < 0.006) discard;
          gl_FragColor = vec4(pow(uColor, vec3(2.2)), a);
        }
      `,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
      polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -3,
    });
    this.mesh = new THREE.InstancedMesh(geo, this.mat, SHADOW_MAX);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;         // over the sea, under the foam/wake
    this.mesh.visible = false;
    parent.add(this.mesh);
  }

  /** Start a frame's worth of shadows. */
  begin(): void { this.n = 0; }

  /**
   * Add one hull's shadow. `(x, seaY, z)` is where the water is under it, `heading` the hull's course,
   * `halfLen`/`halfBeam` its footprint, `lift` how far the hull is riding above the mean sea (crest = the
   * light gets under it, so the patch spreads and pales) and `dark` the base strength (0 = skip).
   */
  add(x: number, seaY: number, z: number, heading: number, halfLen: number, halfBeam: number, lift: number, dark: number): void {
    if (this.n >= SHADOW_MAX || dark <= 0.005) return;
    const spread = 1 + Math.min(0.6, Math.max(-0.25, lift) * 0.30);
    const d = this.dummy;
    d.position.set(x, seaY + 0.06, z);
    d.rotation.set(0, -heading, 0);
    // NOTE these are TOTAL extents, not half-extents: the quad spans [-0.5, 0.5]. Sized under the hull
    // exactly they would be invisible — the ship is opaque and draws over its own shadow — so the blob is
    // deliberately a fifth longer and twice as wide as the hull, which is also what a soft occlusion patch
    // physically is (the penumbra is wider than the occluder).
    d.scale.set(halfLen * 3.0 * spread, 1, halfBeam * 3.6 * spread);
    d.updateMatrix();
    this.mesh.setMatrixAt(this.n, d.matrix);
    this.alpha[this.n] = dark / Math.max(0.6, spread * spread);
    this.n++;
  }

  /** Upload + set the draw range. Zero hulls = `visible = false` = zero draw calls. */
  end(): void {
    this.mesh.count = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n === 0) return;
    this.mesh.instanceMatrix.needsUpdate = true;
    (this.mesh.geometry.getAttribute('aDark') as THREE.BufferAttribute).needsUpdate = true;
  }

  get count(): number { return this.n; }

  dispose(): void {
    this.mesh.parent?.remove(this.mesh);
    this.mesh.geometry.dispose(); this.mat.dispose(); this.mesh.dispose();
  }
}
